import { apiClient } from '../client';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'AGENT' | 'USER' | string;
  status: 'active' | 'inactive' | string;
  preferences?: { menu_permissions?: string[] | null; [key: string]: any };
  avatar_url?: string;
  phone?: string;
  company?: string;
  is_organization_owner?: boolean;
  is_super_admin?: boolean;
  organization_id?: string;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
  has_all_conversations_access?: boolean; // If true, agent sees all conversations (snake_case from backend)
  hasAllConversationsAccess?: boolean;    // CamelCase convenience for frontend
  is_online?: boolean;
  last_seen?: string;
  assigned_accounts?: {
    id: string;
    name: string;
    permissions?: {
      has_all_conversations_access?: boolean;
    };
  }[];
  is_active?: boolean;
  last_login_at?: string;
}

export interface TeamMemberWithAccess extends TeamMember {
  has_all_conversations_access?: boolean;
}

export interface TeamStats {
  total_members: number;
  active_members: number;
  inactive_members: number;
  role_breakdown: { ADMIN: number; AGENT: number; USER: number };
  /** Plan's `users` seat cap. number = cap, -1 = unlimited, null = not resolvable. */
  seat_limit?: number | null;
  /**
   * Per-role seat caps resolved through the authoritative path (plan + add-on
   * + manual override precedence). Each: number = cap, -1 = unlimited,
   * null = legacy plan with no per-role caps (pooled `seat_limit` applies).
   */
  role_limits?: { admin: number | null; user: number | null; agent: number | null };
  /** ACTIVE members per role, INCLUDING the owner — matches the invite gate. */
  role_usage?: { admin: number; user: number; agent: number };
}

export interface InviteMemberData {
  email: string;
  name: string;
  role: 'ADMIN' | 'AGENT' | 'USER';
  businessAccountIds?: string[];
  assignments?: {
    business_account_id: string;
    permissions?: {
      has_all_conversations_access?: boolean;
    };
  }[];
  password?: string; // Optional - if not provided, random password will be generated
  hasAllConversationsAccess?: boolean; // Optional - if true, agent sees all conversations
}

export interface UpdateMemberData {
  id: string;
  name?: string;
  email?: string;
  role?: 'ADMIN' | 'AGENT' | 'USER' | string;
  status?: 'active' | 'inactive' | string;
  phone?: string;
  company?: string;
  hasAllConversationsAccess?: boolean;
  menuPermissions?: string[] | null;
  assignments?: {
    business_account_id: string;
    permissions?: {
      has_all_conversations_access?: boolean;
    };
  }[];
}

export const TeamService = {
  async getTeamMembers(params: { role?: string; status?: string; search?: string; limit?: number; offset?: number } = {}) {
    return apiClient.get<{ success: boolean; data: TeamMember[] }>(`/team/members`, { params });
  },

  async getTeamStats() {
    return apiClient.get<{ success: boolean; data: TeamStats }>(`/team/stats`);
  },

  async inviteMember(payload: InviteMemberData) {
    return apiClient.post(`/team/invite`, payload);
  },

  async updateMember(
    id: string,
    payload: Partial<
      Pick<
        TeamMember,
        'name' | 'email' | 'role' | 'status' | 'phone' | 'company' | 'hasAllConversationsAccess'
      >
    > & { assignments?: UpdateMemberData['assignments']; menuPermissions?: string[] | null }
  ) {
    return apiClient.put(`/team/members/${id}`, payload);
  },

  async removeMember(id: string) {
    return apiClient.delete(`/team/members/${id}`);
  },

  async changeUserPassword(id: string, newPassword: string) {
    return apiClient.put(`/team/members/${id}/password`, { newPassword });
  },
};
